package com.eladfinish.bible.contest.common.usecases

import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.constants.StringConstants.NO_BREAK_SPACE
import com.eladfinish.bible.contest.constants.StringConstants.Regex.WORD_CHAR
import com.eladfinish.bible.contest.extensions.start
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.utils.findOtiotPasukMatches
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.toImmutableList

class MakeOtiotPasukSmallerUseCase {

    companion object {
        const val OTIOT_PASUK_RELATIVE_SIZE = 0.5f

        // `\s` does not match NO-BREAK SPACE on Kotlin/Native (iOS) or OpenJDK (only Android ICU),
        // and displayed content uses NBSP after "פרק"/ot-pasuk — so match NBSP explicitly.
        //
        // `(?<!WORD_CHAR)` rather than `\b`. JS RegExp's \b is ASCII-only (see WORD_CHAR), and on
        // desktop (plain OpenJDK java.util.regex) \b treats a Hebrew letter as a word character
        // but a nikud mark as a non-word one: it fired before a word-final Aleph that follows a
        // vowel point (e.g. "בָּרָא") and never fired after whitespace before a Hebrew letter.
        // WORD_CHAR counts letters and nikud alike, so the result is the same on every platform.
        private val alephOrPerekRegex = "(?<!$WORD_CHAR)(א|פרק)[\\s$NO_BREAK_SPACE]".toRegex()

        // this runs once per ot-pasuk match — keep it compiled once, not per match
        private val excludedWordsRegex = "סוף|ספר|רוב|פרק|א".toRegex()
    }

    operator fun invoke(
        charSequence: CharSequence,
    ): ImmutableList<SpannedRange> {
        val spannedRangeList = mutableListOf<SpannedRange>()
        alephOrPerekRegex.findAll(charSequence).forEach {
            spannedRangeList.add(
                SpannedRange(
                    spanType = SpanType.RelativeSize(proportion = OTIOT_PASUK_RELATIVE_SIZE),
                    textRange = TextRange(start = it.start, end = it.start + it.groups[1]!!.value.length),
                )
            )
        }
        findOtiotPasukMatches(text = charSequence).forEach { match ->
            if (excludedWordsRegex.matches(match.letters.trim())) {
                return@forEach
            }
            // Span ONLY the ot-pasuk letter(s), NOT the trailing whitespace that the regex also
            // matches. Including the trailing NBSP made the letter render far too small on iOS
            // (the size span leaked onto the NBSP that joins the next word). Keep this consistent
            // with the aleph span above, which already spans the letter only.
            spannedRangeList.add(
                SpannedRange(
                    spanType = SpanType.RelativeSize(proportion = OTIOT_PASUK_RELATIVE_SIZE),
                    textRange = TextRange(
                        start = match.lettersStart,
                        end = match.lettersEndExclusive,
                    ),
                )
            )
        }
        return spannedRangeList.toImmutableList()
    }

}
